/**
 * date.js — 프론트엔드 날짜 처리 공통 유틸 (React)
 *
 * ⚠️  new Date() / toISOString() 직접 사용 금지
 *     반드시 이 파일의 함수를 통해 날짜를 처리할 것
 *
 * ── 기본 함수 ──
 * todayKST()              : KST 기준 오늘 날짜 'YYYY-MM-DD'
 * nowKST()                : KST 기준 현재 시각 Date 객체
 * toDateString(value)     : 날짜 값 → 'YYYY-MM-DD' 추출
 * toDateTimeString(value) : 날짜 값 → 'YYYY-MM-DD HH:mm:ss' KST 변환
 *
 * ── 파싱/변환 ──
 * parseYmd(dateStr)        : 'YYYY-MM-DD' → { year, month, day }
 * daysInMonth(year, month) : 해당 월의 일수 (28~31)
 * dayOfWeekUTC(dateStr)    : 'YYYY-MM-DD' → 요일 번호 (0=일 ~ 6=토)
 * currentYearKST()         : KST 기준 현재 연도
 *
 * ── 시간 계산 ──
 * toKSTTimeString(value)           : TIMESTAMPTZ → 'HH:mm' KST
 * diffMinutes(startValue, endValue): 두 시각 간 분 차이
 *
 * ── 날짜 순회 ──
 * eachDayUTC(startYmd, endYmd, cb) : 시작~종료일 순회, cb(ymd, dow) 호출
 *
 * ── 날짜 생성/산술 ──
 * toDateOnlyUtc(value)              : 값 → UTC date-only Date 변환
 * addMonthsClampedUtc(date, months) : UTC Date + 월 (클램핑)
 * addDaysUtc(ymd, n)                : 'YYYY-MM-DD' + n일
 * toKoreanDateTimeString(value)     : 한국어 로케일 날짜시간 문자열
 * isValidYmd(text)                  : 'YYYY-MM-DD' 유효성 검사
 * addMonthsToYmd(ymd, months)       : 'YYYY-MM-DD' + 월 (오버플로)
 *
 * ── 시점 생성/파싱 ──
 * addDaysFromNow(days)    : 현재 + N일 Date 객체 (토큰 만료 등)
 * toEpochMs(value)        : TIMESTAMPTZ → epoch 밀리초
 * parseTimestamp(value)   : TIMESTAMPTZ → Date 객체 (시간 보존)
 */

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

// ── 기본 함수 ──────────────────────────────────────────────────────────────

/** KST 기준 현재 시각 Date 객체 반환 */
export const nowKST = () => new Date(Date.now() + KST_OFFSET_MS);

/** KST 기준 오늘 날짜 문자열 반환 ('YYYY-MM-DD') */
export const todayKST = () => nowKST().toISOString().slice(0, 10);

/**
 * 날짜 값(문자열·Date·null 등)에서 'YYYY-MM-DD' 앞 10자리만 추출
 * - '2026-04-18T15:00:00.000Z' → '2026-04-18'
 * - '2026-04-18' → '2026-04-18'
 * - null/undefined → 'null'/'undefined' (호출부에서 falsy 체크 권장)
 */
export const toDateString = (value) => String(value).slice(0, 10);

/**
 * 날짜 값 → KST 기준 'YYYY-MM-DD HH:mm:ss' 문자열 변환
 * TIMESTAMPTZ 값을 한국 시간으로 표시할 때 사용
 * @param {string|Date} value — ISO 문자열 또는 Date 객체
 * @returns {string} 'YYYY-MM-DD HH:mm:ss' (KST)
 */
export const toDateTimeString = (value) => {
  const dt = (value instanceof Date) ? value : new Date(value);
  const kst = new Date(dt.getTime() + KST_OFFSET_MS);
  const y = kst.getUTCFullYear();
  const M = String(kst.getUTCMonth() + 1).padStart(2, '0');
  const d = String(kst.getUTCDate()).padStart(2, '0');
  const h = String(kst.getUTCHours()).padStart(2, '0');
  const m = String(kst.getUTCMinutes()).padStart(2, '0');
  const s = String(kst.getUTCSeconds()).padStart(2, '0');
  return `${y}-${M}-${d} ${h}:${m}:${s}`;
};

// ── 파싱/변환 ──────────────────────────────────────────────────────────────

/**
 * 'YYYY-MM-DD' 문자열 → { year, month, day } 숫자 분리
 * new Date() 없이 순수 문자열 파싱 — 타임존 버그 원천 차단
 * @param {string} dateStr — 'YYYY-MM-DD' 형식
 * @returns {{ year: number, month: number, day: number }}
 */
export const parseYmd = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return { year: y, month: m, day: d };
};

/**
 * 해당 월의 일수 반환 (28~31)
 * new Date(year, month, 0).getDate() 패턴 대체
 * @param {number} year  — 연도
 * @param {number} month — 월 (1~12)
 * @returns {number}
 */
export const daysInMonth = (year, month) => {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
};

/**
 * 'YYYY-MM-DD' → 요일 번호 반환 (0=일, 1=월, ..., 6=토)
 * new Date(Date.UTC(y,m-1,d)).getUTCDay() 패턴 대체
 * @param {string} dateStr — 'YYYY-MM-DD' 형식
 * @returns {number} 0~6
 */
export const dayOfWeekUTC = (dateStr) => {
  const { year, month, day } = parseYmd(dateStr);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
};

/** KST 기준 현재 연도 반환 */
export const currentYearKST = () => nowKST().getUTCFullYear();

// ── 시간 계산 ──────────────────────────────────────────────────────────────

/**
 * TIMESTAMPTZ 값 → KST 기준 'HH:mm' 문자열 변환
 * check_in/check_out 시각 표시용
 * @param {string|Date} value — ISO 문자열 또는 Date 객체
 * @returns {string} 'HH:mm' (KST)
 */
export const toKSTTimeString = (value) => {
  const dt = (value instanceof Date) ? value : new Date(value);
  const kst = new Date(dt.getTime() + KST_OFFSET_MS);
  const h = String(kst.getUTCHours()).padStart(2, '0');
  const m = String(kst.getUTCMinutes()).padStart(2, '0');
  return `${h}:${m}`;
};

/**
 * 두 시각 간 분(minute) 차이 계산
 * Math.floor((new Date(end) - new Date(start)) / 60000) 패턴 대체
 * @param {string|Date} startValue — 시작 시각
 * @param {string|Date} endValue   — 종료 시각
 * @returns {number} 분 차이 (음수 가능)
 */
export const diffMinutes = (startValue, endValue) => {
  const s = (startValue instanceof Date) ? startValue : new Date(startValue);
  const e = (endValue instanceof Date) ? endValue : new Date(endValue);
  return Math.floor((e.getTime() - s.getTime()) / 60000);
};

// ── 날짜 순회 ──────────────────────────────────────────────────────────────

/**
 * 시작일~종료일까지 하루씩 순회하며 콜백 호출
 * while(d <= e) { d.setUTCDate(d.getUTCDate()+1) } 루프 패턴 대체
 *
 * @param {string} startYmd — 시작일 'YYYY-MM-DD'
 * @param {string} endYmd   — 종료일 'YYYY-MM-DD'
 * @param {(ymd: string, dow: number) => void|boolean} cb
 *   — ymd: 'YYYY-MM-DD', dow: 요일(0=일~6=토)
 *   — cb가 false를 반환하면 순회 중단 (break)
 */
export const eachDayUTC = (startYmd, endYmd, cb) => {
  const s = parseYmd(startYmd);
  const e = parseYmd(endYmd);
  const cursor = new Date(Date.UTC(s.year, s.month - 1, s.day));
  const end = new Date(Date.UTC(e.year, e.month - 1, e.day));
  const pad = (n) => String(n).padStart(2, '0');
  while (cursor <= end) {
    const ymd = `${cursor.getUTCFullYear()}-${pad(cursor.getUTCMonth() + 1)}-${pad(cursor.getUTCDate())}`;
    const dow = cursor.getUTCDay();
    if (cb(ymd, dow) === false) break;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
};

// ── 날짜 생성/산술 ────────────────────────────────────────────────────────

/**
 * 값(Date·문자열 등)을 UTC 자정(00:00:00Z) Date 객체로 변환
 * PostgreSQL DATE 컬럼 값 → JS Date 변환 시 사용 (시간 부분 제거)
 * - Date 객체 → 로컬 날짜(getFullYear/Month/Date) 기준 UTC 자정
 * - 'YYYY-MM-DD' 문자열 → 해당 날짜 UTC 자정
 * - ISO/TIMESTAMPTZ 문자열 → UTC 날짜 부분만 추출 후 UTC 자정
 * @param {Date|string|null} value
 * @returns {Date|null}
 */
export const toDateOnlyUtc = (value) => {
  if (!value) return null;
  // Date 객체 — 로컬 날짜 기준 UTC 자정 (PostgreSQL DATE → JS Date 타임존 보존)
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
  }
  // 'YYYY-MM-DD' 형식 문자열
  const text = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const { year, month, day } = parseYmd(text);
    return new Date(Date.UTC(year, month - 1, day));
  }
  // 그 외 파싱 가능한 문자열 — UTC 날짜 부분만 추출
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()));
};

/**
 * UTC Date 객체에 월(month) 단위 덧셈 (월말 클램핑 적용)
 * 예: 1월 31일 + 1개월 → 2월 28일 (오버플로 방지)
 * 연차 발생일 계산 등 월 단위 산술에 사용
 * @param {Date}   date   — UTC 기준 Date 객체
 * @param {number} months — 더할 개월 수 (음수 가능)
 * @returns {Date} 새 UTC Date 객체
 */
export const addMonthsClampedUtc = (date, months) => {
  const year  = date.getUTCFullYear();
  const month = date.getUTCMonth() + months;
  const day   = date.getUTCDate();
  // 목표 월의 1일로 이동 후 해당 월의 마지막 날 계산
  const first   = new Date(Date.UTC(year, month, 1));
  const lastDay = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  // 원래 일자와 해당 월 마지막 일 중 작은 값 사용 (클램핑)
  return new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), Math.min(day, lastDay)));
};

/**
 * 'YYYY-MM-DD' 문자열에 n일 더하기
 * 달력 이동, 날짜 범위 계산 등에 사용
 * @param {string} ymd — 'YYYY-MM-DD' 형식
 * @param {number} n   — 더할 일수 (음수 가능)
 * @returns {string} 'YYYY-MM-DD'
 */
export const addDaysUtc = (ymd, n) => {
  const { year, month, day } = parseYmd(ymd);
  const dt = new Date(Date.UTC(year, month - 1, day + n));
  const pad = (v) => String(v).padStart(2, '0');
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};

/**
 * 날짜 값 → 한국어 로케일 날짜시간 문자열 변환
 * toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }) 패턴 대체
 * 로그 표시 등 사람이 읽기 쉬운 한국어 형식 출력에 사용
 * @param {string|Date|null} value — ISO 문자열 또는 Date 객체
 * @returns {string} 예: '2026. 4. 18. 14:30:00' 또는 '-'
 */
export const toKoreanDateTimeString = (value) => {
  if (!value) return '-';
  const dt = (value instanceof Date) ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', hour12: false });
};

/**
 * 'YYYY-MM-DD' 문자열 유효성 검사 (new Date() 미사용)
 * 형식 + 범위(월 1~12, 일 1~해당월 일수) 모두 검증
 * @param {string} text
 * @returns {boolean}
 */
export const isValidYmd = (text) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(text || ''))) return false;
  const { year, month, day } = parseYmd(text);
  if (month < 1 || month > 12) return false;
  return day >= 1 && day <= daysInMonth(year, month);
};

/**
 * 'YYYY-MM-DD' 문자열에 월(month) 단위 덧셈 (JS Date 오버플로 방식)
 * 예: 1월 31일 + 1개월 → 3월 3일 (JS Date 기본 동작)
 * addMonthsClampedUtc와 달리 클램핑 없이 JS 기본 오버플로 허용
 * @param {string} ymd    — 'YYYY-MM-DD' 형식
 * @param {number} months — 더할 개월 수 (음수 가능)
 * @returns {string|null} 'YYYY-MM-DD' 또는 유효하지 않으면 null
 */
export const addMonthsToYmd = (ymd, months) => {
  if (!isValidYmd(ymd)) return null;
  const { year, month, day } = parseYmd(ymd);
  const dt = new Date(Date.UTC(year, month - 1 + months, day));
  const pad = (v) => String(v).padStart(2, '0');
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};

// ── 시점 생성/파싱 ────────────────────────────────────────────────────────

/**
 * 현재 시각에서 days일 뒤의 Date 객체 반환
 * 토큰 만료 시각 등 "현재 + N일" 계산에 사용
 * @param {number} days — 더할 일수 (음수 가능)
 * @returns {Date} 미래(또는 과거) 시점 Date
 */
export const addDaysFromNow = (days) => {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000);
};

/**
 * 날짜 값(TIMESTAMPTZ 문자열·Date 등)을 epoch 밀리초(ms)로 변환
 * Date.now() 와 직접 비교하거나 시간 차이 계산 시 사용
 * @param {string|Date|null} value — ISO 문자열 또는 Date 객체
 * @returns {number|null} epoch ms (유효하지 않으면 null)
 */
export const toEpochMs = (value) => {
  if (value == null) return null;
  const dt = (value instanceof Date) ? value : new Date(value);
  const ms = dt.getTime();
  return Number.isFinite(ms) ? ms : null;
};

/**
 * 날짜 값(TIMESTAMPTZ 문자열·Date 등)을 Date 객체로 변환 (시·분·초 보존)
 * toDateOnlyUtc와 달리 시간 정보를 유지 — 출퇴근 시각 표시 등에 사용
 * @param {string|Date|null} value — ISO 문자열 또는 Date 객체
 * @returns {Date|null} 파싱된 Date 객체 (유효하지 않으면 null)
 */
export const parseTimestamp = (value) => {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

// default export 없음 — named export만 사용
